import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { ButtonProps } from '@/components/ui/button';
import { Button } from '@/components/ui/button';

/**
 * Submit button with `Button`'s `loading` behavior while its form submits: it stays focusable, shows a spinner in
 * place of `icon`, and a press meanwhile does not submit again, including Enter in a field.
 */
export function SubmitButton({
  submitting,
  icon,
  children,
  ...props
}: Omit<ButtonProps, 'type' | 'loading' | 'asChild'> & {
  submitting: boolean;
  icon?: ReactNode;
}) {
  return (
    <Button {...props} type="submit" loading={submitting}>
      {submitting ? null : icon}
      {children}
    </Button>
  );
}

/**
 * Returns a ref and a `request` function. After `request` is called, the element of the ref is focused after the
 * first render in which it is mounted. A form's submission error callout renders only after its error is set, so
 * `request` is called with `setError`.
 */
export function useFocusAfterRender<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const requested = useRef(false);
  useEffect(() => {
    if (!requested.current || !ref.current) return;
    requested.current = false;
    ref.current.focus();
  });
  const request = useCallback(() => {
    requested.current = true;
  }, []);
  return [ref, request] as const;
}
